import React, { useEffect, useState } from 'react';
import { ReportPeriod } from '../../../ReportBuilder/Components/SectionPeriod/utils';
import {
  mapIndicators,
  ReportAPIResponse,
  SectionIndicators,
  sortIndicatorsIntoSections,
} from '../../../ReportBuilder/Components/IndicatorDetails/utils';
import { useAllServiceCollections } from 'shared/apollo/pageHooks/useAllServiceCollections';
import {
  getIndicatorById,
  getInitialSelectedIndicatorList,
  IndicatorByService,
  IndicatorGroup,
  IndicatorSectionKey,
  ServiceGroup,
} from '../../../ReportBuilder/Components/ImpactIndicator/utils';
import { useAllIndicatorsMin, useClient } from 'shared/apollo';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  FeedbackCommentBodyWithIndex,
  IMPACT_SUMMARY_QUESTION_ID,
} from '../../utils';
import { parseImpactSummaryAnswer } from '../../FeedbackDetails/ImpactSummaryDetails';
import ImpactTable, { ImpactTableProps } from './ImpactTable';
import TrueImpactIconButton from 'shared/design-system/components/TrueImpactIconButton';
import { faClose } from '@fortawesome/pro-light-svg-icons';
import {
  RequestState,
  RequestStateSuccess,
} from 'shared/auth/hooks/useRequestState';
import {
  addSuccessCriteriaToIndicators,
  getInitialImpactSummaryValues,
  getSuccessCriteriaIndicators,
  IndicatorWithUpdatedText,
} from '../../../ReportBuilder/Components/ImpactSummaryTable/IndicatorStages';
import {
  CAPACITY_DEVELOPMENT_QUESTION_ID,
  REACH_QUESTION_ID,
  SOCIAL_IMPACT_QUESTION_ID,
} from '../../../ReportBuilder/Components/SectionMultiRadio';
import {
  ACT_QUESTION_ID,
  LEARN_QUESTION_ID,
} from '../../../ReportBuilder/Components/ImpactIndicator/SectionIndicatorStage';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { getReportIndicators } from '../../../ReportBuilder/Components/IndicatorDetails/api';
import { getIndicatorsUpdatedText } from '../../../ReportBuilder/Components/ImpactIndicator/api';
import {
  ReportStage,
  ReportStatus,
} from 'donor/portal/pages/FeedbackDashboard/api';

const getImpactSummaryAnswers = (
  serviceCollections: ServiceGroup[],
  indicatorsData: IndicatorGroup[],
  requestState: RequestStateSuccess<{
    [id: number]: Answer;
  }>,
  selectedIndicators: SectionIndicators,
) => {
  const allServices =
    serviceCollections?.filter(
      coll => coll.name === requestState.data[40002]?.text,
    )?.[0]?.services || [];
  const service = allServices?.filter(
    ser =>
      ser.name === requestState.data[40003]?.text && ser.version === 'v4',
  )?.[0];

  const capacityIndicators = parseIndicators(
    requestState.data[CAPACITY_DEVELOPMENT_QUESTION_ID]?.text,
  );
  const reachIndicators = parseIndicators(
    requestState.data[REACH_QUESTION_ID]?.text,
  );
  const succeedIndicators = parseIndicators(
    requestState.data[SOCIAL_IMPACT_QUESTION_ID]?.text,
  );
  const initialLearnEnabled =
    requestState.data[LEARN_QUESTION_ID]?.text === 'Yes';
  const initialActEnabled = requestState.data[ACT_QUESTION_ID]?.text === 'Yes';

  const impactAnswer = requestState.data?.[IMPACT_SUMMARY_QUESTION_ID];
  const initialImpactAnswer = parseImpactSummaryAnswer(impactAnswer?.text);
  const isEmpty = isIndicatorsEmpty(selectedIndicators);

  const capacityDevelopmentEnabled = !isEmpty
    ? selectedIndicators.capacityDevelopment.length > 0
    : capacityIndicators.length > 0;
  const learnEnabled = !isEmpty
    ? selectedIndicators.learn.length > 0
    : initialLearnEnabled;
  const actEnabled = !isEmpty
    ? selectedIndicators.act.length > 0
    : initialActEnabled;

  const allIndicators = getIndicatorById(
    indicatorsData,
    service?.dbId,
    capacityDevelopmentEnabled,
    learnEnabled,
    actEnabled,
  );
  const impact =
    initialImpactAnswer ||
    generateImpactModel({
      initialValue: impactAnswer?.text,
      allIndicators,
      capacityIndicators,
      reachIndicators,
      succeedIndicators,
      learnEnabled,
      actEnabled,
    });

  return {
    service,
    impact,
    allIndicators,
    capacityDevelopmentEnabled,
    learnEnabled,
    actEnabled,
    capacityIndicators,
    reachIndicators,
    succeedIndicators,
  };
};

const isIndicatorsEmpty = (i: SectionIndicators): boolean => {
  for (const k of Object.keys(i) as IndicatorSectionKey[]) {
    if (i[k].length > 0) return false;
  }
  return true;
};

const parseIndicators = (a: string | string[] | undefined): string[] => {
  if (!a) return [];
  if (Array.isArray(a)) return a;
  try {
    return JSON.parse(a);
  } catch (error) {
    return [];
  }
};

const generateImpactModel = ({
  initialValue,
  allIndicators,
  capacityIndicators,
  reachIndicators,
  succeedIndicators,
  learnEnabled,
  actEnabled,
}: {
  initialValue: string | undefined;
  allIndicators: IndicatorByService;
  capacityIndicators: string[];
  reachIndicators: string[];
  succeedIndicators: string[];
  learnEnabled: boolean;
  actEnabled: boolean;
}) => {
  const initial = getInitialImpactSummaryValues(
    [],
    succeedIndicators,
    capacityIndicators,
    reachIndicators,
    allIndicators,
  );
  const { selected } = getInitialSelectedIndicatorList(
    initial,
    allIndicators,
    capacityIndicators.length > 0,
    learnEnabled,
    actEnabled,
    'capacityDevelopment',
  );

  return selected;
};

const style: React.CSSProperties = {
  top: 30,
  right: 20,
  position: 'fixed',
};

const errorComponent = (
  message = 'Unable to load answers.',
  handleClose: () => unknown,
) => (
  <TrueImpactText color="critical" align="center">
    <TrueImpactIconButton
      icon={faClose}
      label="Close"
      style={style}
      onClick={handleClose}
    />
    {message}
  </TrueImpactText>
);

interface ImpactModelProps {
  reportId: number;
  programName: string;
  isReportPassed: boolean;
  reportStage: ReportStage;
  reportStatus: ReportStatus;
  reportPeriod: ReportPeriod;
  impactDetailAnswerId: number | undefined;
  selectedIndicators: SectionIndicators;
  serviceType: string | undefined;
  serviceGoal: string | undefined;
  intermediaries: string | undefined;
  beneficiaryTerm: string | undefined;
  feedback: FeedbackCommentBodyWithIndex[];
  requestState: RequestState<{
    [id: number]: Answer;
  }>;
  handleBack: () => unknown;
  handleDone: () => unknown;
  handleClose: () => unknown;
  onNextSection: () => unknown;
  reloadAnswers: () => unknown;
  handleSaveAnswer: (questionId: number, answer: string) => unknown;
}

const ImpactModel: React.FC<ImpactModelProps> = ({
  selectedIndicators,
  reportId,
  feedback,
  programName,
  reportStage,
  reportStatus,
  requestState,
  isReportPassed,
  impactDetailAnswerId,
  handleBack,
  handleDone,
  handleClose,
  reloadAnswers,
  onNextSection,
  handleSaveAnswer,
}) => {
  const client = useClient();
  const {
    data: indicatorsData,
    loading: loadingIndicators,
  } = useAllIndicatorsMin();
  const { data: services, loading: loadingServices } = useAllServiceCollections(
    {
      reportId,
    },
  );
  const serviceCollections = (services as unknown) as ServiceGroup[];
  const [reportRequestState, makeReportRequest] = useGraphQLRequest<{
    allReports: [ReportAPIResponse];
  }>();
  const [indicatorsWithUpdatedTextRequest, makeRequest] = useGraphQLRequest<{
    allReports: [
      {
        dbId: number;
        results: IndicatorWithUpdatedText[];
      },
    ];
  }>();

  useEffect(() => {
    if (indicatorsWithUpdatedTextRequest.type !== 'REQUEST_INIT') {
      return;
    }
    makeRequest(getIndicatorsUpdatedText(client!, reportId));
  }, [indicatorsWithUpdatedTextRequest]);

  const isLoading =
    loadingIndicators ||
    loadingServices ||
    requestState.type === 'REQUEST_INIT' ||
    requestState.type === 'REQUEST_START' ||
    indicatorsWithUpdatedTextRequest.type === 'REQUEST_INIT' ||
    indicatorsWithUpdatedTextRequest.type === 'REQUEST_START';

  const impactAnswer = isLoading
    ? undefined
    : requestState.data?.[IMPACT_SUMMARY_QUESTION_ID];
  const initialImpactAnswer = isLoading
    ? undefined
    : parseImpactSummaryAnswer(impactAnswer?.text);

  useEffect(() => {
    if (
      isLoading ||
      (initialImpactAnswer && !isIndicatorsEmpty(selectedIndicators)) ||
      reportRequestState.type !== 'REQUEST_INIT'
    ) {
      return;
    }
    makeReportRequest(getReportIndicators(client!, reportId));
  }, [reportId, isLoading, initialImpactAnswer]);

  if (isLoading || reportRequestState.type === 'REQUEST_START') {
    return <TrueImpactLoading />;
  }

  if (
    requestState.type === 'REQUEST_ERROR' ||
    indicatorsWithUpdatedTextRequest.type === 'REQUEST_ERROR'
  ) {
    return errorComponent(undefined, handleClose);
  }

  const {
    impact,
    service,
    actEnabled,
    learnEnabled,
    allIndicators,
    capacityIndicators,
    reachIndicators,
    succeedIndicators,
    capacityDevelopmentEnabled,
  } = getImpactSummaryAnswers(
    serviceCollections,
    indicatorsData,
    requestState,
    selectedIndicators,
  );

  if (!impact) {
    return errorComponent('Unable to load the impact model.', handleClose);
  }

  return (
    <ImpactModelRender
      reportId={reportId}
      service={service}
      feedback={feedback}
      programName={programName}
      isReportPassed={isReportPassed}
      reportStage={reportStage}
      reportStatus={reportStatus}
      impactDetails={selectedIndicators}
      allIndicators={allIndicators}
      impactSummary={impact}
      impactDetailAnswerId={impactDetailAnswerId}
      impactSummaryAnswerId={impactAnswer?.dbId || undefined}
      handleClose={handleClose}
      handleDone={handleDone}
      reloadAnswers={reloadAnswers}
      learnEnabled={learnEnabled}
      actEnabled={actEnabled}
      requestState={requestState}
      capacityDevelopmentEnabled={capacityDevelopmentEnabled}
      reportRequestState={reportRequestState}
      indicatorsData={indicatorsData}
      serviceCollections={serviceCollections}
      capacityIndicators={capacityIndicators}
      reachIndicators={reachIndicators}
      succeedIndicators={succeedIndicators}
      indicatorsWithUpdatedTextRequest={indicatorsWithUpdatedTextRequest}
    />
  );
};

interface ImpactModelRenderProps extends ImpactTableProps {
  isReportPassed: boolean;
  requestState: RequestStateSuccess<{
    [id: number]: Answer;
  }>;
  reportRequestState: RequestState<{
    allReports: [ReportAPIResponse];
  }>;
  reportStage: ReportStage;
  reportStatus: ReportStatus;
  capacityDevelopmentEnabled: boolean;
  learnEnabled: boolean;
  actEnabled: boolean;
  capacityIndicators: string[];
  reachIndicators: string[];
  succeedIndicators: string[];
  indicatorsWithUpdatedTextRequest: RequestStateSuccess<{
    allReports: [
      {
        dbId: number;
        results: IndicatorWithUpdatedText[];
      },
    ];
  }>;
  indicatorsData: IndicatorGroup[];
  serviceCollections: ServiceGroup[];
  handleClose: () => unknown;
  reloadAnswers: () => unknown;
}

const ImpactModelRender: React.FC<ImpactModelRenderProps> = ({
  reportId,
  service,
  feedback,
  reportStage,
  reportStatus,
  programName,
  isReportPassed,
  impactDetails,
  allIndicators,
  impactSummary,
  capacityDevelopmentEnabled,
  learnEnabled,
  actEnabled,
  requestState,
  reportRequestState,
  impactDetailAnswerId,
  impactSummaryAnswerId,
  capacityIndicators,
  reachIndicators,
  succeedIndicators,
  indicatorsData,
  serviceCollections,
  indicatorsWithUpdatedTextRequest,
  handleClose,
  handleDone,
  reloadAnswers,
}) => {
  const [defaultIndicators, setDefaultIndicators] = useState<
    IndicatorByService
  >(allIndicators);

  const isExistingAnswer = Boolean(
    impactSummaryAnswerId && impactDetailAnswerId,
  );
  const [impact, setImpact] = useState<IndicatorByService>(impactSummary);
  const [indicators, setIndicators] = useState<SectionIndicators>(
    impactDetails,
  );

  useEffect(() => {
    const response = getSuccessCriteriaIndicators(
      indicatorsWithUpdatedTextRequest,
    );
    if (response === undefined) return;
    const { indicators, indicatorIds } = response;
    const defaultIndicators = addSuccessCriteriaToIndicators({
      currentState: allIndicators,
      indicatorIds,
      indicators,
      actEnabled: true,
      learnEnabled: true,
      capacityDevelopmentEnabled: true,
      // actEnabled: actEnabled,
      // learnEnabled: learnEnabled,
      // capacityDevelopmentEnabled: capacityDevelopmentEnabled,
    });
    setDefaultIndicators(defaultIndicators);
    const r = getImpactSummaryAnswers(
      serviceCollections,
      indicatorsData,
      requestState,
      impactDetails,
    );
    setImpact(r.impact);
  }, [indicatorsWithUpdatedTextRequest]);

  useEffect(() => {
    if (reportRequestState.type !== 'REQUEST_SUCCESS') return;
    if (!reportRequestState.data.allReports[0].indicators.length) {
      const result: ReportAPIResponse['indicators'] = [];
      for (const k of Object.keys(impactSummary) as IndicatorSectionKey[]) {
        impactSummary[k].forEach(x => {
          result.push({
            dbId: x.id,
            definition: x.definition,
            isHuman: x.isHuman,
            name: x.name,
            stageId: x.stageId,
          });
        });
      }
      reportRequestState.data.allReports[0].indicators = result;
    }

    const rawIndicators = mapIndicators(
      reportRequestState,
      // true,
      // true,
      // true,
      capacityDevelopmentEnabled,
      learnEnabled,
      actEnabled,
      isReportPassed,
    );

    const indicatorSections = sortIndicatorsIntoSections(rawIndicators);

    setIndicators(indicatorSections);
  }, [reportRequestState]);

  if (isIndicatorsEmpty(indicators)) {
    return <TrueImpactLoading />;
  }

  return (
    <ImpactTable
      reportId={reportId}
      service={service}
      feedback={feedback}
      reportStage={reportStage}
      reportStatus={reportStatus}
      programName={programName}
      impactDetails={indicators}
      allIndicators={defaultIndicators}
      impactSummary={impact}
      impactDetailAnswerId={impactDetailAnswerId}
      impactSummaryAnswerId={impactSummaryAnswerId}
      handleClose={isExistingAnswer ? handleClose : undefined}
      handleDone={handleDone}
      reloadAnswers={reloadAnswers}
    />
  );
};

export default ImpactModel;
